import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Modal, Box, SelectChangeEvent, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import {
  getCoutSupplementairesByActionId,
  deleteCoutSupplementaire,
  updateCoutSupplementaire,
} from '../../redux/CoutSupplementaire/coutSupplementaireAction'
import Selectetat from '../../components/common/SelectEtat'
import UseAlert from '../../components/common/alert'
import CoutSupplementaireCreate from './CoutSuppCreate'
import {
  DetailButton,
  DetailEntityPanel,
  DetailTable,
} from '../../components/common/detail'
import {
  canManageMission,
  DetailRowActions,
} from '../../components/common/detail/DetailRowActions'
import {
  ListPageDateCell,
  ListPagePill,
  STATUS_PILL,
} from '../../components/common/ListPageCells'
import { formatCurrency, locale } from '../../utils/formatNumbers'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import { selectError as selectCoutSuppError } from '../../redux/CoutSupplementaire/coutSupplementaireSelector'
import { clearCoutSupplementaireError } from '../../redux/CoutSupplementaire/coutSupplementaireSlice'
import '../../components/common/ListPageTable.css'

interface CoutSupplementaireListeProps {
  idaction: string
  onDataChange?: () => void
}

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 420,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 3,
  borderRadius: '12px',
}

const ETAT_PILL: Record<string, { bg: string; color: string }> = {
  ...STATUS_PILL,
  EnCours: { bg: '#E6EFF8', color: '#22548A' },
  Approuvé: { bg: '#E1F4F0', color: '#076B5C' },
  Approuve: { bg: '#E1F4F0', color: '#076B5C' },
  Refusé: { bg: '#FAE6E3', color: '#96271B' },
  Refuse: { bg: '#FAE6E3', color: '#96271B' },
}

const CoutSupplementaireListe: React.FC<CoutSupplementaireListeProps> = ({
  idaction,
  onDataChange,
}) => {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useAppDispatch()
  const roles = useSelector((state: any) => state.auth.roles)
  const currency = useSelector(selectSpecifications)[0]?.Currency
  const coutSupplementairesList = useSelector(
    (state: RootState) => state.coutSupplementaire.coutSupplementaires
  )
  const loading = useSelector(
    (state: RootState) => state.coutSupplementaire.loading
  )
  const apiError = useSelector(selectCoutSuppError)

  const [openCreate, setOpenCreate] = useState(false)
  const [openEditModal, setOpenEditModal] = useState(false)
  const [selectedRowData, setSelectedRowData] = useState<any>(null)
  const [search, setSearch] = useState('')
  const [Etat, setEtat] = useState<string>('En attente')
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  useEffect(() => {
    if (idaction) dispatch(getCoutSupplementairesByActionId(idaction))
  }, [dispatch, idaction])

  useEffect(() => {
    if (apiError) {
      setAlert({ open: true, message: apiError, severity: 'error' })
    }
  }, [apiError])

  const rows = useMemo(() => {
    const list = Array.isArray(coutSupplementairesList)
      ? coutSupplementairesList
      : []
    const q = search.trim().toLowerCase()
    if (!q) return list
    return list.filter((r: any) => {
      const amount = String(r.Montant ?? '')
      const etat = String(r.Etat ?? '').toLowerCase()
      return amount.includes(q) || etat.includes(q) || t(r.Etat).toLowerCase().includes(q)
    })
  }, [coutSupplementairesList, search, t])

  const handleCloseAlert = () => {
    setAlert((prev) => ({ ...prev, open: false }))
    if (alert.severity === 'error') dispatch(clearCoutSupplementaireError())
  }

  const handleDeleteClick = async (CoutSupplementaireId: string) => {
    try {
      await dispatch(deleteCoutSupplementaire(CoutSupplementaireId)).unwrap()
      dispatch(getCoutSupplementairesByActionId(idaction))
      onDataChange?.()
    } catch (error) {
      console.error('Failed to delete supplementary cost:', error)
    }
  }

  const handleEditClick = (row: any) => {
    setSelectedRowData(row)
    setEtat(row.Etat || 'En attente')
    setOpenEditModal(true)
  }

  const handleUpdateEtat = async () => {
    try {
      if (selectedRowData?.Etat && selectedRowData?._id) {
        await dispatch(
          updateCoutSupplementaire({
            updatedCoutSupplementaire: {
              CoutSupplementaireId: selectedRowData.CoutSupplementaireId,
              DateHeure: selectedRowData.DateHeure,
              Etat,
              Montant: selectedRowData.Montant,
              _id: selectedRowData._id,
              ActionId: selectedRowData.ActionId,
            },
            _id: selectedRowData._id,
          })
        ).unwrap()

        setAlert({
          open: true,
          message: t('alerts.coutSuppUpdate.success'),
          severity: 'success',
        })
        dispatch(getCoutSupplementairesByActionId(selectedRowData.ActionId))
        onDataChange?.()
        setOpenEditModal(false)
      } else {
        setAlert({
          open: true,
          message: t('alerts.coutSuppUpdate.emptyFields'),
          severity: 'warning',
        })
      }
    } catch (error) {
      console.error('Failed to update supplementary cost state:', error)
    }
  }

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />
      <DetailEntityPanel
        listTitle={t('tables.AdditionalCostTable.title')}
        searchPlaceholder={t('filterByName', { defaultValue: 'Filter' })}
        searchValue={search}
        onSearchChange={setSearch}
        addLabel={t('buttons.addDemand')}
        onAdd={() => setOpenCreate(true)}
        canAdd={canManageMission(roles)}
      >
        <DetailTable<any>
          loading={loading && !rows.length}
          loadingLabel={t('loading')}
          emptyTitle={t('missionDetailsPage.empty.title', {
            defaultValue: 'Nothing here yet',
          })}
          emptyMessage={t('taskDetailsPage.empty.costs', {
            defaultValue: 'No additional cost recorded for this task yet.',
          })}
          rows={rows}
          rowKey={(r) => r._id as string}
          columns={[
            {
              key: 'montant',
              header: t('tables.AdditionalCostTable.columns.Montant'),
              render: (r) => (
                <span className="detail-amount">
                  {formatCurrency(r.Montant ?? 0, currency, {
                    locale,
                    useIntl: true,
                  })}
                </span>
              ),
            },
            {
              key: 'date',
              header: t('tables.AdditionalCostTable.columns.DateHeure'),
              render: (r) => (
                <ListPageDateCell value={r.DateHeure} locale={locale} />
              ),
            },
            {
              key: 'etat',
              header: t('tables.AdditionalCostTable.columns.Etat'),
              render: (r) => {
                const style = ETAT_PILL[r.Etat] || {
                  bg: '#EAF0F0',
                  color: '#5F7484',
                }
                return (
                  <ListPagePill
                    label={t(r.Etat, { defaultValue: r.Etat })}
                    bg={style.bg}
                    color={style.color}
                  />
                )
              },
            },
          ]}
          actionsHeader={t('tables.AdditionalCostTable.columns.Action')}
          renderActions={(r) => (
            <DetailRowActions
              roles={roles}
              onEdit={() => handleEditClick(r)}
              onDelete={() => handleDeleteClick(r._id as string)}
              deleteItem={{
                entityType: t(
                  'taskDetailsPage.activitiesAccordion.tabs.additionalCost',
                  { defaultValue: 'Additional Cost' }
                ),
                name:
                  r.Montant != null
                    ? formatCurrency(r.Montant, currency, {
                        locale,
                        useIntl: true,
                      })
                    : undefined,
                details: [
                  {
                    label: t('deleteConfirm.fields.status'),
                    value: r.Etat || '—',
                  },
                ],
              }}
            />
          )}
        />
      </DetailEntityPanel>

      <Modal open={openEditModal} onClose={() => setOpenEditModal(false)}>
        <Box sx={modalStyle}>
          <h4 style={{ margin: '0 0 16px' }}>{t('popups.changeState.title')}</h4>
          <Stack spacing={2}>
            <Selectetat
              value={Etat}
              onChange={(e: SelectChangeEvent<string>) =>
                setEtat(String(e.target.value))
              }
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <DetailButton onClick={handleUpdateEtat}>
                {t('popups.changeState.updateButton')}
              </DetailButton>
              <DetailButton
                variant="ghost"
                onClick={() => setOpenEditModal(false)}
              >
                {t('buttons.cancel')}
              </DetailButton>
            </div>
          </Stack>
        </Box>
      </Modal>

      <Modal open={openCreate} onClose={() => setOpenCreate(false)}>
        <Box sx={{ ...modalStyle, width: 480 }}>
          <CoutSupplementaireCreate
            onClose={() => setOpenCreate(false)}
            ActionId={idaction}
            onSuccess={() => {
              dispatch(getCoutSupplementairesByActionId(idaction))
              onDataChange?.()
            }}
          />
        </Box>
      </Modal>
    </>
  )
}

export default CoutSupplementaireListe
